import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';

import useFilters from '@/hooks/useFilters';
import {kycService} from '@/services/kycService';
import {userService} from '@/services/userService';
import type {
  AdminAfriexPerson,
  AfriexQueryParams,
  AfriexVerificationStatus,
} from '@/types';

import type {KYCListProps} from './types';

const useKYCList = (): KYCListProps => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status') as AfriexVerificationStatus;

  const [userList, setUserList] = useState<AdminAfriexPerson[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [userTotal, setUserTotal] = useState<number>(0);
  const [showPagination, setShowPagination] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);
  const {applyFilters} = useFilters(setCurrentFilters);

  // Layer the `status` URL param onto the filters the request is built from
  // instead of mirroring it into `currentFilters` state via an effect. That
  // effect ran after the first render and always handed `currentFilters` a
  // fresh object identity, which recreated `fetchAllUsers` and fired a
  // second, unfiltered-then-filtered request on every mount. `currentFilters`
  // now holds only the filters the admin picked, so its identity is stable
  // and this memo is stable with it.
  const effectiveFilters = useMemo(
    () => (status ? {...currentFilters, status} : currentFilters),
    [currentFilters, status],
  );

  const fetchAllUsers = useCallback(
    async (params?: AfriexQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const {data: users, total} = await kycService.getKYCList({
          ...params,
          ...effectiveFilters,
        });
        setUserList(users);
        setUserTotal(total || 0);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [effectiveFilters],
  );

  useEffect(() => {
    // Fire-and-forget: fetchAllUsers try/catches, stores the failure in
    // `error` and clears `isLoading`, so this only stops an unexpected throw
    // becoming an unhandled rejection.
    fetchAllUsers({}).catch(() => {});
  }, [fetchAllUsers]);

  const handleApplyFilters = useCallback(() => {
    // Layer the URL `status` onto a copy instead of mutating the state
    // object. `applyFilters` receives exactly the same filters as before;
    // the difference is that `currentFilters` is no longer written behind
    // React's back, so it stays owned by `selectCurrentFilters`.
    const applied = applyFilters(
      status ? {...filtersToApply, status} : filtersToApply,
    );
    // Pagination reset is success-only work. applyFilters returns false when it
    // rejects a date range, and Pagination reacts to this flag by calling
    // onPageChange -> a refetch, so toggling it unconditionally fired a request
    // the user never asked for. See hooks/useFilters.ts.
    if (!applied) {
      return false;
    }

    setIsPageReset((prev) => !prev);
    return true;
  }, [applyFilters, filtersToApply, status]);

  // Handle change for instant data fetching
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  const selectAppliedFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const handleAutoComplete = useCallback(
    async (searchTerm: string) => {
      try {
        setError(null);
        setIsLoading(true);
        setShowPagination(false);
        if (!searchTerm?.trim() || searchTerm.length < 3) {
          await fetchAllUsers({});
          return;
        }
        const users = await userService.searchAutoComplete(searchTerm);
        if (users) {
          setUserList(users);
          setUserTotal(1);
          setIsSearch(true);
        }
      } catch (e) {
        setError(e);
      }
      setIsLoading(false);
    },
    [fetchAllUsers],
  );

  const cleanFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
    if (status) {
      setCurrentFilters({status});
    }
  };

  // `UserListProps.handleAutoComplete` is declared `=> void` and the value is
  // handed to SearchBar, whose own prop is declared the same way, so wrap
  // rather than widen. handleAutoComplete already try/catches into `error`;
  // the catch only stops an unexpected throw becoming an unhandled rejection.
  // useCallback keeps the identity stable across renders.
  const onAutoComplete = useCallback(
    (searchTerm: string) => {
      handleAutoComplete(searchTerm).catch(() => {});
    },
    [handleAutoComplete],
  );

  return {
    error,
    fetchUserList: fetchAllUsers,
    isLoading,
    isSearch,
    setPage,
    pageInfo: {
      page,
      total: userTotal,
    },
    showPagination,
    userList,
    filterCount,
    isPageReset,
    currentFilters, // automatically triggers fetch
    filtersToApply, // requires handleApplyFilters to trigger fetch
    clearFilters: cleanFilters,
    selectAppliedFilters, // replaces handleDateChange
    handleApplyFilters,
    selectCurrentFilters, // replaces handleFilterChange
    handleAutoComplete: onAutoComplete,
  };
};

export default useKYCList;
